import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Common";
import { band } from "@/components/ResultScore";
import { useAuth } from "@/context/AuthContext";
import { api, asList, enc, errMsg } from "@/lib/api";
import { bestResume, useAtsHistory } from "@/lib/atsScores";

export const BestResume = ({ jobId }) => {
  const { profile, refreshProfile } = useAuth();
  const best = bestResume(useAtsHistory(jobId));
  const [versions, setVersions] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (best) api.get("/profile/resume/versions").then((r) => setVersions(asList(r.data, "versions"))).catch(() => setVersions([])); }, [!!best]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!best) return null;
  const v = (versions || []).find((x) => x.name === best.resume);
  const isCurrent = profile?.resume_file?.name === best.resume;
  const switchTo = async () => {
    if (!v) return toast.message("One-tap switching turns on once resume versions are live on the backend.");
    setBusy(true);
    try {
      await api.post(`/profile/resume/versions/${enc(v.version_id)}/restore`);
      await refreshProfile();
      toast.success(`Now using "${v.label || v.name}"`);
      window.dispatchEvent(new CustomEvent("rpopro:resume-replaced", { detail: { skipJobId: jobId } }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-[#D4AF37]/30 bg-[#09090B] p-3 sm:flex-row sm:items-center" data-testid="best-resume">
      <Crown className="hidden h-4 w-4 shrink-0 text-[#D4AF37] sm:block" />
      <div className="min-w-0 flex-1 text-sm text-zinc-300">
        Best resume for this job: <span className="font-medium text-[#F3C649]" data-testid="best-resume-name">{v?.label || best.resume}</span>{" "}
        <span className="font-mono" style={{ color: band(best.score).c }}>{best.score}%</span>
        {isCurrent && <span className="ml-1 text-xs text-zinc-500">· in use</span>}
      </div>
      {!isCurrent && <Button size="sm" onClick={switchTo} disabled={busy} data-testid="best-resume-switch">{busy ? <Spinner /> : <RotateCcw className="h-3.5 w-3.5" />}Switch to it</Button>}
    </div>
  );
};
